import { AlertTriangle, CheckCircle2, CircleDashed } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { BusinessProcessVersion, ProcedureReadinessItem } from './types'

function formatDate(value: string | null) {
  if (!value) return null
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function ReadinessItems({ title, items }: { title: string; items: ProcedureReadinessItem[] }) {
  if (items.length === 0) return null

  return (
    <div>
      <p className="text-xs font-medium uppercase text-muted-foreground">{title}</p>
      <ul className="mt-2 grid gap-1.5 text-sm md:grid-cols-2">
        {items.map((item) => (
          <li key={item.key} className="flex gap-2">
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-current opacity-60" />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function ProcedureReadinessPanel({ version }: { version: BusinessProcessVersion | null }) {
  if (!version?.procedure_generated) return null

  const readiness = version.procedure_readiness
  const checkedAt = formatDate(readiness.checked_at)
  const isReady = readiness.status === 'ready'
  const needsInformation = readiness.status === 'needs_information'
  const icon = isReady ? <CheckCircle2 className="size-4" /> : needsInformation ? <AlertTriangle className="size-4" /> : <CircleDashed className="size-4" />
  const badgeVariant = isReady ? 'success' : 'secondary'

  return (
    <section className="mt-5 space-y-4 border-t pt-5" data-testid="procedure-readiness-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold">
          {icon}
          Recomendações do processo
        </div>
        <Badge variant={badgeVariant}>{readiness.status_label}</Badge>
        {checkedAt && <span className="text-xs text-muted-foreground">Verificado em {checkedAt}</span>}
      </div>

      {isReady ? (
        <p className="text-sm text-muted-foreground">Todas as recomendações avaliadas foram atendidas.</p>
      ) : needsInformation ? (
        <p className="text-sm text-muted-foreground">As melhorias abaixo são recomendadas, mas não bloqueiam a aprovação nem a disponibilização ao agente.</p>
      ) : (
        <p className="text-sm text-muted-foreground">As recomendações serão calculadas quando o documento humano for importado. A avaliação não bloqueia a aprovação.</p>
      )}

      <ReadinessItems title="Melhorias recomendadas" items={readiness.missing_items} />
      <ReadinessItems title="Verificado" items={readiness.passed_items} />
    </section>
  )
}
